// components/ProjectFrame.tsx — H3a (COCKPIT-HOME-COLUMNS-SPEC.md §H3): a
// project screen's own framed live site (Studio Stays' public front page,
// VoiceStrip's site). NOT Rexx or Origin Care — neither is ever framed
// (Core's own `frame-ancestors 'none'` on the Rexx Box; the briefing room's
// own CSP) — those screens never mount this component at all.
//
// The brief's own attribute set, verbatim, pinned by
// ProjectFrame.test.tsx:
//   sandbox="allow-scripts allow-same-origin allow-forms allow-popups
//            allow-popups-to-escape-sandbox"
//   referrerpolicy="no-referrer"
//   loading="lazy"
// Deliberately NO `allow-top-navigation` or
// `allow-top-navigation-by-user-activation` of any kind, in either
// direction — a framed page must never be able to navigate the cockpit
// tab itself.
//
// A visible origin line above the frame ("host/path · as of <load time>")
// and a Reload button (the brief's own requirement); a frame that fails —
// `onError`, or no `load` within the timeout (a CSP/frame-ancestors
// refusal fires neither reliably across browsers) — shows "<host> did not
// answer" in place of the iframe, never a blank box.
import { useEffect, useRef, useState } from 'preact/hooks';
import './ProjectFrame.css';

const LOAD_TIMEOUT_MS = 8_000;

const SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

function originLine(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    return `${u.host}${path}`;
  } catch {
    return url;
  }
}

export interface ProjectFrameProps {
  url: string;
  title: string;
}

export function ProjectFrame({ url, title }: ProjectFrameProps) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [asOf, setAsOf] = useState<Date | null>(null);
  const [nonce, setNonce] = useState(0);
  const timerRef = useRef<number | null>(null);
  // ⚑ Found by this file's own test (ProjectFrame.test.tsx, "a frame that
  // fails to load"): effects run on a LATER pass than the render that
  // scheduled them, never synchronously with it. A mount's own effect used
  // to open with `setStatus('loading')` unconditionally — harmless on its
  // own, except that a same-tick `onError` (dispatched right after mount,
  // exactly what the test does, and exactly what a genuinely-instant
  // refusal would look like) queues `status: 'error'` BEFORE that effect
  // has had its turn, and the effect's own `setStatus('loading')` then
  // ran AFTER and clobbered it back to 'loading' — a frame that failed
  // instantly reported itself as still loading, forever. This ref makes
  // the reset-to-loading fire only on a REAL re-arm (`url`/`nonce`
  // actually changing after mount — Reload, or a new project), never on
  // the mount's own first pass, where the initial `useState('loading')`
  // already holds the correct value and needs no effect to restate it.
  const armedOnce = useRef(false);

  function clearTimer() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  useEffect(() => {
    if (armedOnce.current) {
      setStatus('loading');
    } else {
      armedOnce.current = true;
    }
    clearTimer();
    timerRef.current = window.setTimeout(() => setStatus((cur) => (cur === 'loading' ? 'error' : cur)), LOAD_TIMEOUT_MS);
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, nonce]);

  function handleLoad() {
    clearTimer();
    setStatus('loaded');
    setAsOf(new Date());
  }

  function handleError() {
    clearTimer();
    setStatus('error');
  }

  function reload() {
    setNonce((n) => n + 1);
  }

  const asOfLabel = asOf ? asOf.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

  return (
    <div class="pframe" data-testid="project-frame">
      <div class="pframe__origin">
        <span class="pframe__origin-text">
          {originLine(url)}
          {asOfLabel ? ` · as of ${asOfLabel}` : ''}
        </span>
        <button type="button" class="pframe__reload" onClick={reload}>
          Reload
        </button>
      </div>
      <div class="pframe__body">
        {status === 'error' ? (
          <div class="pframe__error" role="status">
            {originLine(url)} did not answer.
          </div>
        ) : (
          <iframe
            key={nonce}
            class="pframe__iframe"
            src={url}
            title={title}
            sandbox={SANDBOX}
            referrerpolicy="no-referrer"
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
      </div>
    </div>
  );
}
